/**
 * Invite Service (global)
 *
 * Shared invite lookup/validation API used by the logged-in applicant Property tab
 * (Angular lookupInvite / validateInvite parity). Lives in the global API layer so
 * features consume a shared service instead of importing from one another.
 */

import apiClient, { type ApiError, type ApiResponse } from '@/lib/api/apiClient'
import type { ExtendedAxiosRequestConfig } from '@/lib/api/types'
import { API_ENDPOINTS } from '@/lib/api/config'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type { InviteDetails } from '@/types/invite'

const INVITE_QUERY_PARAMS = {
  page: 'register',
} as const

/** Error body from GET /invite/:code during registration (Angular `error.error`). */
interface InviteRegistrationErrorBody {
  message?: string
  email?: string
  error?: string
}

export type InviteRegistrationError = ApiError & {
  email?: string
  modalTitle: string
  confirmAction: 'ok' | 'signIn'
}

export interface InvitationData {
  [key: string]: unknown
}

export interface InvitationResponse {
  success: boolean
  message?: string
  data?: InvitationData
}

interface InviteLookupParams {
  email?: string
  mobile_no?: string
  first_name?: string
  last_name?: string
  birthdate?: string
  security_number?: string
}

function buildLookUpQuery(params: InviteLookupParams): string {
  const queryParams = new URLSearchParams()
  if (params.email) queryParams.append('email', params.email)
  if (params.mobile_no) {
    const cleanedMobile = params.mobile_no.replace(/\D/g, '')
    queryParams.append('mobile_no', cleanedMobile)
  }
  if (params.first_name) queryParams.append('first_name', params.first_name)
  if (params.last_name) queryParams.append('last_name', params.last_name)
  if (params.birthdate) queryParams.append('birthdate', params.birthdate)
  if (params.security_number) queryParams.append('security_number', params.security_number)
  return queryParams.toString()
}

/**
 * GET /look_up for the logged-in applicant Property tab (Angular lookupInvite parity).
 * Surfaces HTTP failures to the caller instead of swallowing them.
 */
export async function lookupInviteForApplication(
  params: InviteLookupParams,
): Promise<InviteDetails[]> {
  const q = buildLookUpQuery(params)
  if (!q) {
    throw { message: 'Email or mobile number is required to look up an invite.', status: 400 } as ApiError
  }
  try {
    const response = await apiClient.get<InviteDetails[]>(
      `/look_up?${q}`,
      {
        skipAuth: true,
      } as ExtendedAxiosRequestConfig,
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error: unknown) {
    throw {
      message: extractErrorMessage(error, 'Invite lookup failed.'),
      status: (error as ApiError)?.status || 400,
    } as ApiError
  }
}

/**
 * GET /invite/:code for resident Property "Validate" (Angular validateInvite parity).
 * Sends auth when present; same path as registration invite fetch.
 */
export async function fetchInviteForResidentApplication(inviteId: string): Promise<InviteDetails> {
  const trimmed = inviteId.trim()
  if (!trimmed) {
    throw { message: 'Please enter an invite code.', status: 400 } as ApiError
  }
  try {
    const response = await apiClient.get<InviteDetails>(
      `/invite/${encodeURIComponent(trimmed)}`,
    )
    return response.data
  } catch (error: unknown) {
    throw {
      message: extractErrorMessage(error, 'Invalid or expired invite code.'),
      status: (error as ApiError)?.status || 400,
    } as ApiError
  }
}

/**
 * Get invitation data by invite ID for apply-now registration flow.
 * Tries /invite/{id} then /coapplicant_invite/{id} on 404.
 */
export async function getInvitationData(inviteId: string): Promise<InvitationResponse> {
  if (!inviteId || !inviteId.trim()) {
    throw {
      message: 'Invite code is required',
      status: 400,
    } as ApiError
  }

  const trimmedId = inviteId.trim()

  try {
    try {
      return await fetchInviteEndpoint(API_ENDPOINTS.INVITE_DETAILS(trimmedId))
    } catch (primaryError) {
      const primaryApiError = primaryError as ApiError
      if (primaryApiError.status === 404) {
        return await fetchInviteEndpoint(API_ENDPOINTS.COAPPLICANT_INVITE_DETAILS(trimmedId))
      }
      throw primaryError
    }
  } catch (error) {
    throw parseInviteRegistrationApiError(error)
  }
}

/**
 * Maps invite lookup failures to Angular register `getInvitationData` error dialogs.
 */
export function parseInviteRegistrationApiError(error: unknown): InviteRegistrationError {
  const apiError = error as ApiError & {
    data?: InviteRegistrationErrorBody | string
  }
  const status = apiError.status || 400

  if (typeof apiError.data === 'string' && apiError.data.trim()) {
    const message = apiError.data.trim()
    if (
      message.includes('The Invitation Code has already been used. Please Sign-in')
    ) {
      return {
        message,
        status,
        modalTitle: 'Invitation Error',
        confirmAction: 'signIn',
      }
    }
    return {
      message,
      status,
      modalTitle: 'Validation Message',
      confirmAction: 'ok',
    }
  }

  const responseData = apiError.data
  if (responseData && typeof responseData === 'object') {
    if (responseData.email) {
      return {
        message:
          responseData.message ||
          'User is already registered with this email.',
        status,
        email: responseData.email,
        modalTitle: 'Validation Message',
        confirmAction: 'signIn',
      }
    }

    const errorMessage =
      responseData.message ||
      responseData.error ||
      apiError.message ||
      ''
    const message =
      typeof errorMessage === 'string' ? errorMessage : String(errorMessage)

    if (message.includes('already been used')) {
      return {
        message,
        status,
        modalTitle: 'Invitation Error',
        confirmAction: 'signIn',
      }
    }

    if (
      message.includes(
        'User is already registered with this email. Please Sign-in with your email and password to start application',
      )
    ) {
      return {
        message,
        status,
        modalTitle: 'Validation Message',
        confirmAction: 'signIn',
      }
    }

    if (message.includes('already registered')) {
      return {
        message,
        status,
        modalTitle: 'Validation Message',
        confirmAction: 'signIn',
      }
    }

    if (message) {
      return {
        message,
        status,
        modalTitle: 'Validation Message',
        confirmAction: 'ok',
      }
    }
  }

  const fallbackMessage =
    status === 401 || status === 403 || status === 404
      ? 'Invalid invite code'
      : apiError.message && !apiError.message.includes('status code')
        ? apiError.message
        : 'Failed to get invitation data'

  return {
    message: fallbackMessage,
    status,
    modalTitle: 'Validation Message',
    confirmAction: 'ok',
  }
}

async function fetchInviteEndpoint(endpoint: string): Promise<InvitationResponse> {
  const response = await apiClient.get<InvitationData | ApiResponse<InvitationData>>(
    endpoint,
    {
      params: INVITE_QUERY_PARAMS,
      headers: {
        Accept: 'application/json, text/plain, */*',
      },
      skipAuth: true,
    } as ExtendedAxiosRequestConfig,
  )

  const responseData = response.data

  if (responseData && typeof responseData === 'object' && 'success' in responseData) {
    const wrappedResponse = responseData as ApiResponse<InvitationData>
    return {
      success: wrappedResponse.success ?? true,
      message: wrappedResponse.message,
      data: (wrappedResponse.data as InvitationData) || undefined,
    }
  }

  return {
    success: true,
    data: responseData as InvitationData,
  }
}
